import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/hooks/useContent";
import { useProfile, useUserState, useToggleAction } from "@/hooks/useUserData";
import { ProfileBars } from "@/components/ProfileBars";
import { ActionItem } from "@/components/ActionItem";
import { PillarIcon } from "@/components/PillarIcon";
import { Flame, Sparkles, ArrowRight, Bell, Instagram } from "lucide-react";
import { buildWhatsAppMessage } from "@/components/WhatsAppBox";

export function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: content } = useContent();
  const { data: profile, isLoading: pl } = useProfile();
  const { data: state } = useUserState();
  const toggle = useToggleAction();

  useEffect(() => {
    if (!pl && profile && !profile.has_test) navigate("/test", { replace: true });
  }, [pl, profile, navigate]);

  if (pl || !content || !profile || !profile.has_test) {
    return <div className="flex justify-center pt-20"><div className="w-9 h-9 rounded-full border-4 border-[#6B2247] border-t-[#E32C8C] animate-spin" /></div>;
  }

  const actions = state?.actions || {};
  const streak = state?.streak || 0;
  const pri = content.pillars[profile.priority];
  const gestoId = `${profile.priority}-0`;
  const firstName = (user?.name || "").split(" ")[0] || "";

  const countDone = (pillar) =>
    content.pillars[pillar].actions.reduce((n, _, i) => n + (actions[`${pillar}-${i}`] ? 1 : 0), 0);

  const waMessage = buildWhatsAppMessage(profile, content.pillars);
  const waLink = `https://wa.me/${content.whatsapp_number}?text=${encodeURIComponent(waMessage)}`;

  return (
    <div className="bw-fade">
      {/* Greeting */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <p className="text-sm text-[#EAC6D9]">Bentornata,</p>
          <h1 className="font-serif text-4xl font-bold text-[#F6DCEA] leading-none" data-testid="home-greeting">
            {firstName || "bellezza"}
          </h1>
        </div>
        <div
          data-testid="streak-badge"
          className="flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card"
        >
          <Flame className="w-4 h-4 text-[#E32C8C]" />
          <span className="text-sm font-bold text-[#F6DCEA]">{streak}</span>
          <span className="text-xs text-[#EAC6D9]">giorni</span>
        </div>
      </div>

      {/* Gentle reminder banner */}
      {!actions[gestoId] && (
        <div
          data-testid="home-reminder-banner"
          className="flex items-center gap-3 rounded-2xl p-4 mb-4 bg-gradient-to-r from-[#2A0A1B] to-[#401431] border border-[#E32C8C]/35 bw-card"
        >
          <span className="w-10 h-10 rounded-full bg-[#E32C8C]/15 flex items-center justify-center shrink-0">
            <Bell className="w-5 h-5 text-[#E32C8C]" />
          </span>
          <p className="text-sm text-[#F6DCEA] leading-snug">
            Il tuo <span className="font-semibold">gesto di oggi</span> ti aspetta. Un piccolo passo per te. 💗
          </p>
        </div>
      )}

      {/* Profile card */}
      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4 shadow-[0_4px_20px_rgba(74,18,48,0.05)]" data-testid="home-profile-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${pri.color}1A` }}>
              <PillarIcon pillar={profile.priority} className="w-4 h-4" style={{ color: pri.color }} />
            </span>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#EAC6D9] font-semibold">Archetipo</p>
              <p className="font-serif text-xl font-semibold text-[#F6DCEA] leading-none">{profile.archetype.name}</p>
            </div>
          </div>
        </div>
        <ProfileBars profile={profile} pillars={content.pillars} pillarOrder={content.pillar_order} compact />
      </div>

      {/* Gesto di oggi */}
      <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-5 mb-4" data-testid="gesto-oggi">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#E32C8C]" />
          <h2 className="font-serif text-2xl font-semibold text-[#F6DCEA]">Il gesto di oggi</h2>
        </div>
        <ActionItem
          label={pri.actions[0]} checked={!!actions[gestoId]}
          onToggle={() => toggle.mutate({ action_id: gestoId, done: !actions[gestoId] })}
          testId="gesto-oggi-action" color={pri.color}
        />
      </div>

      {/* Pillars grid */}
      <h2 className="font-serif text-2xl font-semibold text-[#F6DCEA] mb-3 px-1">I tuoi 4 pilastri</h2>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {content.pillar_order.map((p) => {
          const meta = content.pillars[p];
          const done = countDone(p);
          return (
            <div
              key={p}
              data-testid={`home-pillar-${p}`}
              className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4"
            >
              <span className="w-10 h-10 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${meta.color}1A` }}>
                <PillarIcon pillar={p} className="w-5 h-5" style={{ color: meta.color }} />
              </span>
              <p className="font-serif text-xl font-semibold text-[#F6DCEA] leading-tight">{meta.name}</p>
              <p className="text-xs text-[#EAC6D9] mb-2">{meta.subtitle}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-[#6B2247] overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(done / 5) * 100}%`, backgroundColor: meta.color }} />
                </div>
                <span className="text-xs font-bold" style={{ color: meta.color }}>{done}/5</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Academy banner */}
      <button
        onClick={() => navigate("/app/academy")}
        data-testid="home-academy-banner"
        className="w-full text-left rounded-2xl p-5 mb-4 text-white shadow-lg active:scale-[0.99] transition-all"
        style={{ background: "linear-gradient(135deg,#E32C8C 0%,#8E1A52 55%,#3A0C21 100%)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1 opacity-90">
              <Sparkles className="w-4 h-4" />
              <span className="text-xs uppercase tracking-widest font-semibold">Academy</span>
            </div>
            <p className="font-serif text-2xl font-bold">Entra nella tua community</p>
            <p className="text-sm text-white/85 mt-1">Professioniste, gruppi ed eventi solo per te</p>
          </div>
          <ArrowRight className="w-6 h-6 shrink-0" />
        </div>
      </button>

      {/* Consulenza banner */}
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="home-consulenza-banner"
        className="w-full flex items-center gap-3 rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4"
      >
        <span className="w-12 h-12 rounded-full overflow-hidden shrink-0 border-2 border-[#6B2247]">
          <img src="/elena.jpg?v=2" alt="Elena" className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
        </span>
        <div className="flex-1">
          <p className="font-semibold text-[#F6DCEA]">Consulenza con Elena</p>
          <p className="text-xs text-[#EAC6D9]">Scrivile su WhatsApp per ripartire da te</p>
        </div>
        <ArrowRight className="w-5 h-5 text-[#EAC6D9]" />
      </a>

      {/* Instagram footer link */}
      <a
        href="https://www.instagram.com/bewoman_academy"
        target="_blank"
        rel="noopener noreferrer"
        data-testid="home-instagram-link"
        className="mt-6 mb-2 flex items-center justify-center gap-2 text-sm text-[#EAC6D9] hover:text-[#FF8FC7] transition-colors"
      >
        <Instagram className="w-4 h-4" />
        <span>Seguici <span className="font-semibold text-[#F6DCEA]">@bewoman_academy</span></span>
      </a>
    </div>
  );
}
